import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2, Edit2, Shield, Users as UsersIcon, ClipboardCheck, Briefcase } from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access', icon: Shield, color: '#D35400' },
  { value: 'appraiser', label: 'Appraiser', desc: 'Field inspections', icon: ClipboardCheck, color: '#4A6FA5' },
  { value: 'office_staff', label: 'Office Staff', desc: 'Manage clients & quotes', icon: Briefcase, color: '#2F855A' },
];

export default function UserManagementPage() {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editId) {
        await api.put(`/users/${editId}`, form);
      } else {
        if (!form.password) { setError('Password required'); return; }
        await api.post('/users', form);
      }
      setShowForm(false);
      setEditId(null);
      setForm({});
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error saving user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const getRoleMeta = (role) => ROLES.find(r => r.value === role) || ROLES[2];

  return (
    <div data-testid="user-management-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">User Management</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{users.length}</span> {users.length === 1 ? 'user' : 'users'}</p>
        </div>
        <button data-testid="add-user-button" onClick={() => { setForm({ role: 'office_staff' }); setEditId(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-orange-500/20">
          <Plus size={16} strokeWidth={2.5} /> Add User
        </button>
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map(u => {
            const meta = getRoleMeta(u.role);
            const isMe = u._id === currentUser?._id;
            return (
              <div key={u._id} data-testid={`user-card-${u._id}`} className="bg-white border border-[#E2E2DF] rounded-md p-5 card-lift relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: meta.color }} />
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ring-1" style={{ backgroundColor: `${meta.color}15`, borderColor: `${meta.color}30` }}>
                      <meta.icon size={18} style={{ color: meta.color }} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{u.name}{isMe && <span className="text-[10px] text-slate-400 font-normal ml-1.5">(You)</span>}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  {!isMe && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`edit-user-${u._id}`} onClick={() => { setForm({ name: u.name, email: u.email, role: u.role }); setEditId(u._id); setShowForm(true); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"><Edit2 size={14} /></button>
                      <button data-testid={`delete-user-${u._id}`} onClick={() => handleDelete(u._id)} className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full ring-1" style={{ backgroundColor: `${meta.color}12`, color: meta.color, borderColor: `${meta.color}25` }}>{meta.label}</span>
              </div>
            );
          })}
        </div>
      )}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E2E2DF]">
              <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{editId ? 'Edit User' : 'New User'}</h2>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Name *</label>
                <input data-testid="user-name-input" required value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Email *</label>
                <input data-testid="user-email-input" type="email" required value={form.email || ''} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{editId ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input data-testid="user-password-input" type="password" value={form.password || ''} onChange={e => setForm({...form, password: e.target.value})} required={!editId} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:ring-2 focus:ring-[#D35400] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Role</label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map(r => (
                    <button key={r.value} type="button" onClick={() => setForm({...form, role: r.value})} className={`p-3 rounded-md border text-center transition-all ${form.role === r.value ? 'border-[#D35400] bg-[#D35400]/5' : 'border-[#E2E2DF] hover:border-slate-300'}`}>
                      <r.icon size={18} className="mx-auto mb-1" style={{ color: form.role === r.value ? r.color : '#8A8D93' }} />
                      <p className="text-xs font-semibold text-slate-700">{r.label}</p>
                      <p className="text-[10px] text-slate-400">{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
                <button data-testid="save-user-button" type="submit" className="px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">Save User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
